import type { SkinSeoulAdminProduct } from '../../../../models/ProductTypes';

export interface ProductChanges {
  name?: { old: string; new: string };
  status?: { old: string; new: string };
  postStatus?: { old: string; new: string };
  stockStatus?: { old: string; new: string };
  categories?: { old: string[]; new: string[] };
  brand?: { old: string; new: string };
  size?: { old: string; new: string };
  color?: { old: string; new: string };
  pack?: { old: string; new: string };
  attributes?: Record<string, { old: string; new: string }>;
}

export const detectProductChanges = (
  currentProduct: SkinSeoulAdminProduct,
  originalProduct: SkinSeoulAdminProduct | null,
): ProductChanges | null => {
  if (!originalProduct) {
    return null;
  }

  const changes: ProductChanges = {};
  let hasChanges = false;

  // Check basic fields
  if (currentProduct.name !== originalProduct.name) {
    changes.name = { old: originalProduct.name, new: currentProduct.name };
    hasChanges = true;
  }

  if (currentProduct.status !== originalProduct.status) {
    changes.status = { old: originalProduct.status || '', new: currentProduct.status || '' };
    hasChanges = true;
  }

  if (currentProduct.postStatus !== originalProduct.postStatus) {
    changes.postStatus = { old: originalProduct.postStatus || '', new: currentProduct.postStatus || '' };
    hasChanges = true;
  }

  if (JSON.stringify(currentProduct.category) !== JSON.stringify(originalProduct.category)) {
    changes.categories = { old: originalProduct.category || [], new: currentProduct.category || [] };
    hasChanges = true;
  }

  if (currentProduct.brand !== originalProduct.brand) {
    changes.brand = { old: originalProduct.brand || '', new: currentProduct.brand || '' };
    hasChanges = true;
  }

  if (currentProduct.size !== originalProduct.size) {
    changes.size = { old: originalProduct.size || '', new: currentProduct.size || '' };
    hasChanges = true;
  }

  if (currentProduct.color !== originalProduct.color) {
    changes.color = { old: originalProduct.color || '', new: currentProduct.color || '' };
    hasChanges = true;
  }

  if (currentProduct.pack !== originalProduct.pack) {
    changes.pack = { old: originalProduct.pack || '', new: currentProduct.pack || '' };
    hasChanges = true;
  }

  // Check attributes
  const attributeChanges: Record<string, { old: string; new: string }> = {};

  const attributesToCheck = [
    'ss_name_kor',
    'highlight',
    'score',
    'cp_url',
    'backup_sources',
    'official_store_urls',
    'preferred_source',
    'attribute_size',
    'attribute_color',
    'attribute_pack',
    'gp_product_id',
    'cp_parent_code',
    'wave',
    'ss_stock_locked',
  ];

  attributesToCheck.forEach((attr) => {
    const currentValue = currentProduct.attributes?.[attr];
    const originalValue = originalProduct.attributes?.[attr];

    if (currentValue !== originalValue) {
      attributeChanges[attr] = { old: originalValue || '', new: currentValue || '' };
      hasChanges = true;
    }
  });

  if (Object.keys(attributeChanges).length > 0) {
    changes.attributes = attributeChanges;
  }

  return hasChanges ? changes : null;
};

export const buildChangedAttributesPayload = (
  currentProduct: SkinSeoulAdminProduct,
  originalProduct: SkinSeoulAdminProduct | null,
): Record<string, string> => {
  const changes = detectProductChanges(currentProduct, originalProduct);

  if (!changes) {
    return {};
  }

  // Convert the changes to the payload format (only new values)
  const changedAttributes: Record<string, string> = {};

  // Add attribute changes if any
  if (changes.attributes) {
    Object.entries(changes.attributes).forEach(([key, value]) => {
      changedAttributes[key] = value.new;
    });
  }

  // Also map direct property changes to attribute fields
  if (changes.size) {
    changedAttributes['attribute_size'] = changes.size.new;
  }
  if (changes.color) {
    changedAttributes['attribute_color'] = changes.color.new;
  }
  if (changes.pack) {
    changedAttributes['attribute_pack'] = changes.pack.new;
  }

  return changedAttributes;
};
